<template>
  <div>
    <PageTitleVue> MONTAR RELATÓRIO </PageTitleVue>

    <section class="space-y-5">
      <div class="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
        <div
          class="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between"
        >
          <div>
            <button
              type="button"
              class="mb-3 inline-flex items-center gap-2 text-sm font-medium text-gray-500 transition hover:text-[#2f8f2f]"
              @click="goBack"
            >
              <PhArrowLeft class="h-4 w-4" />
              Voltar para relatórios
            </button>

            <div class="text-start" v-if="report">
              <div class="flex flex-wrap items-center gap-3">
                <h1 class="text-2xl font-bold text-gray-900">
                  {{ report.title }}
                </h1>

                <ReportStatusBadge :status="report.status" />
              </div>

              <p class="mt-1 text-sm text-gray-500">
                {{ clientName }} • {{ formatDate(report.visit_date) }} •
                {{ getReportTypeLabel(report.report_type) }}
              </p>
            </div>

            <div v-else class="space-y-2">
              <div class="h-7 w-72 animate-pulse rounded-lg bg-gray-100"></div>
              <div class="h-4 w-96 animate-pulse rounded-lg bg-gray-100"></div>
            </div>
          </div>

          <div v-if="report" class="flex flex-wrap items-center gap-2">
            <button
              v-if="report.status === 'finished'"
              type="button"
              class="inline-flex items-center gap-2 rounded-xl border border-yellow-100 bg-yellow-50 px-4 py-2.5 text-sm font-semibold text-yellow-700 transition hover:bg-yellow-100"
              @click="reopenReport"
            >
              <PhArrowCounterClockwise class="h-5 w-5" />
              Reabrir como rascunho
            </button>

            <button
              v-if="report"
              type="button"
              class="inline-flex items-center gap-2 rounded-xl border border-green-100 bg-white px-4 py-2.5 text-sm font-semibold text-[#2f8f2f] shadow-sm transition hover:bg-green-50"
              @click="previewReport"
            >
              <PhEye class="h-5 w-5" />
              Pré-visualizar
            </button>

            <button
              v-if="isEditable"
              type="button"
              class="inline-flex items-center gap-2 rounded-xl border border-green-100 bg-white px-4 py-2.5 text-sm font-semibold text-[#2f8f2f] shadow-sm transition hover:bg-green-50"
              @click="openCreateItemDrawer"
            >
              <PhPlus class="h-5 w-5" />
              Novo item
            </button>

            <button
              v-if="isEditable && items.length"
              type="button"
              class="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-[#2f8f2f] to-[#5dbb45] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:shadow-md"
              @click="finishReport"
            >
              <PhCheckCircle class="h-5 w-5" />
              Finalizar relatório
            </button>
          </div>
        </div>
      </div>

      <div
        v-if="report && !isEditable"
        class="rounded-2xl border border-yellow-100 bg-yellow-50/70 p-4 text-start"
      >
        <div class="flex gap-3">
          <div
            class="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-yellow-600 shadow-sm"
          >
            <PhWarningCircle class="h-5 w-5" />
          </div>

          <div>
            <h3 class="text-sm font-bold text-yellow-800">Edição bloqueada</h3>

            <p class="mt-1 text-sm text-yellow-700">
              Este relatório não está em rascunho. Os itens técnicos estão em
              modo de visualização. Para alterar o conteúdo, reabra o relatório
              como rascunho quando a opção estiver disponível.
            </p>
          </div>
        </div>
      </div>

      <div class="grid grid-cols-1 gap-5 xl:grid-cols-12">
        <main class="xl:col-span-8">
          <div
            class="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm"
          >
            <div class="mb-5 flex items-center justify-between gap-4 text-start">
              <div>
                <h2 class="text-lg font-bold text-gray-800">
                  Itens do relatório
                </h2>

                <p class="text-sm text-gray-500">
                  Organize os pontos observados durante a visita técnica.
                </p>
              </div>

              <span
                class="rounded-full bg-green-50 px-3 py-1 text-xs font-semibold text-green-700"
              >
                {{ items.length }} item(ns)
              </span>
            </div>

            <div
              v-if="isEditable && builderWarnings.length"
              class="mb-5 rounded-2xl border border-yellow-100 bg-yellow-50/70 p-4 text-start"
            >
              <div class="flex gap-3">
                <div
                  class="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white text-yellow-600 shadow-sm"
                >
                  <PhWarningCircle class="h-5 w-5" />
                </div>

                <div>
                  <h3 class="text-sm font-bold text-yellow-800">
                    Pontos de atenção antes da finalização
                  </h3>

                  <ul class="mt-2 space-y-1 text-sm text-yellow-700">
                    <li
                      v-for="warning in builderWarnings"
                      :key="warning"
                      class="list-disc ml-4"
                    >
                      {{ warning }}
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div v-if="loadingItems" class="space-y-4">
              <div
                v-for="index in 3"
                :key="index"
                class="h-40 animate-pulse rounded-2xl bg-gray-100"
              ></div>
            </div>

            <ReportEmptyState
              v-else-if="!items.length"
              title="Nenhum item adicionado"
              description="Adicione pontos observados, fotos, recomendações e prioridades para montar o corpo técnico do relatório."
              :icon="PhListChecks"
            >
              <template v-if="isEditable" #action>
                <button
                  type="button"
                  class="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-[#2f8f2f] to-[#5dbb45] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:shadow-md"
                  @click="openCreateItemDrawer"
                >
                  <PhPlus class="h-5 w-5" />
                  Adicionar primeiro item
                </button>
              </template>
            </ReportEmptyState>

            <div v-else class="space-y-4">
              <ReportVisitItemCard
                v-for="(item, index) in items"
                :key="item.id"
                :item="item"
                :index="index"
                :total="items.length"
                :read-only="!isEditable"
                @edit="openEditItemDrawer"
                @remove="removeItem"
                @move-up="moveItem(index, -1)"
                @move-down="moveItem(index, 1)"
              />
            </div>
          </div>
        </main>

        <aside class="xl:col-span-4">
          <div class="sticky top-5 space-y-4">
            <!-- resumo -->
            <div
              class="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm"
            >
              <h3 class="text-base font-bold text-gray-800 uppercase">
                Resumo do relatório
              </h3>

              <div class="mt-5 space-y-4 text-sm">
                <div class="flex justify-between gap-4">
                  <span class="text-gray-400">Empresa</span>
                  <span class="text-right font-medium text-gray-700">
                    {{ clientName }}
                  </span>
                </div>

                <div class="flex justify-between gap-4">
                  <span class="text-gray-400">Técnico</span>
                  <span class="text-right font-medium text-gray-700">
                    {{ report?.technician_name || "-" }}
                  </span>
                </div>

                <div class="flex justify-between gap-4">
                  <span class="text-gray-400">Tipo</span>
                  <span class="text-right font-medium text-gray-700">
                    {{ getReportTypeLabel(report?.report_type) }}
                  </span>
                </div>

                <div class="flex justify-between gap-4">
                  <span class="text-gray-400">Itens</span>
                  <span class="text-right font-medium text-gray-700">
                    {{ items.length }}
                  </span>
                </div>

                <div class="flex justify-between gap-4">
                  <span class="text-gray-400">Status</span>
                  <ReportStatusBadge :status="report?.status || 'draft'" />
                </div>
              </div>
            </div>

            <!-- preenchimento -->
            <div
              class="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm"
            >
              <h3 class="text-base font-bold text-gray-800 uppercase">
                Qualidade do preenchimento
              </h3>

              <div class="mt-5 grid grid-cols-2 gap-3">
                <div class="rounded-2xl bg-green-50 p-4">
                  <div class="flex items-center gap-2 text-green-700">
                    <PhClipboardText class="h-5 w-5" />
                    <span class="text-xs font-semibold uppercase"> Itens </span>
                  </div>

                  <p class="mt-2 text-2xl font-bold text-green-800">
                    {{ itemStats.total }}
                  </p>
                </div>

                <div class="rounded-2xl bg-blue-50 p-4">
                  <div class="flex items-center gap-2 text-blue-700">
                    <PhImageSquare class="h-5 w-5" />
                    <span class="text-xs font-semibold uppercase">
                      Com foto
                    </span>
                  </div>

                  <p class="mt-2 text-2xl font-bold text-blue-800">
                    {{ itemStats.withPhoto }}
                  </p>
                </div>

                <div class="rounded-2xl bg-yellow-50 p-4">
                  <div class="flex items-center gap-2 text-yellow-700">
                    <PhWarningCircle class="h-5 w-5" />
                    <span class="text-xs font-semibold uppercase">
                      Sem foto
                    </span>
                  </div>

                  <p class="mt-2 text-2xl font-bold text-yellow-800">
                    {{ itemStats.withoutPhoto }}
                  </p>
                </div>

                <div class="rounded-2xl bg-red-50 p-4">
                  <div class="flex items-center gap-2 text-red-700">
                    <PhWarningCircle class="h-5 w-5" />
                    <span class="text-xs font-semibold uppercase">
                      Incompletos
                    </span>
                  </div>

                  <p class="mt-2 text-2xl font-bold text-red-800">
                    {{ itemStats.withoutTechnicalContent }}
                  </p>
                </div>
              </div>
            </div>

            <div class="rounded-2xl border border-green-100 bg-green-50/60 p-5 text-start">
              <!-- <h3 class="text-sm font-bold text-gray-800">Próximas etapas</h3> -->

              <p class="mt-2 text-sm text-gray-600">
                Use a pré-visualização para conferir a capa, layout, fotos e organização dos itens antes de gerar o PDF final.  
              </p>
            </div>
          </div>
        </aside>
      </div>
    </section>

    <ReportVisitItemFormDrawer
      v-model="isItemDrawerOpen"
      :item="selectedItem"
      :loading="isSavingItem"
      @submit="saveItem"
      @close="clearSelectedItem"
    />
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import {
  PhArrowCounterClockwise,
  PhArrowLeft,
  PhCheckCircle,
  PhClipboardText,
  PhEye,
  PhImageSquare,
  PhListChecks,
  PhPlus,
  PhWarningCircle,
} from "@phosphor-icons/vue";

import PageTitleVue from "@/components/Titles/PageTitle.vue";
import { showToast as toast } from "@/helpers/UseToasts";

import ReportsService from "./services/reports.service";
import ReportVisitItemsService from "./services/reportVisitItems.service";

import ReportStatusBadge from "./components/reports/ReportStatusBadge.vue";
import ReportEmptyState from "./components/shared/ReportEmptyState.vue";
import ReportVisitItemCard from "./components/builder/ReportVisitItemCard.vue";
import ReportVisitItemFormDrawer from "./components/builder/ReportVisitItemFormDrawer.vue";

const route = useRoute();
const router = useRouter();

const report = ref(null);
const items = ref([]);

const loadingItems = ref(false);
const isItemDrawerOpen = ref(false);
const isSavingItem = ref(false);
const selectedItem = ref(null);

const reportId = computed(() => route.params.id);

const isEditable = computed(() => {
  return report.value?.status === "draft";
});

const clientName = computed(() => {
  return (
    report.value?.client_snapshot?.trade_name ||
    report.value?.client_snapshot?.corporate_name ||
    report.value?.client?.trade_name ||
    report.value?.client?.corporate_name ||
    "-"
  );
});

const hasPhotographicBody = computed(() => {
  return ["photographic", "photo_text"].includes(report.value?.report_type);
});

const itemStats = computed(() => {
  const total = items.value.length;

  const withPhoto = items.value.filter((item) => {
    return Boolean(item.photo_url || item.photo_path);
  }).length;

  const withoutPhoto = total - withPhoto;

  const withRisk = items.value.filter((item) => {
    return Boolean(item.risk_level);
  }).length;

  const withoutTechnicalContent = items.value.filter((item) => {
    return isItemWithoutTechnicalContent(item);
  }).length;

  return {
    total,
    withPhoto,
    withoutPhoto,
    withRisk,
    withoutTechnicalContent,
  };
});

const builderWarnings = computed(() => {
  const warnings = [];

  if (!items.value.length) {
    warnings.push("Nenhum item técnico foi adicionado ao relatório.");
  }

  if (itemStats.value.withoutTechnicalContent > 0) {
    warnings.push(
      `${itemStats.value.withoutTechnicalContent} item(ns) estão sem conteúdo técnico suficiente.`
    );
  }

  if (hasPhotographicBody.value && itemStats.value.withoutPhoto > 0) {
    warnings.push(
      `${itemStats.value.withoutPhoto} item(ns) estão sem foto. Em relatórios fotográficos, isso pode enfraquecer o documento.`
    );
  }

  return warnings;
});

onMounted(async () => {
  await Promise.all([loadReport(), loadItems()]);
});

async function loadReport() {
  try {
    const response = await ReportsService.getById(reportId.value);
    report.value = response?.data?.data || response?.data || null;
  } catch (error) {
    console.error(error);

    toast(
      "Protege informa:",
      "Não foi possível carregar o relatório.",
      "error"
    );

    goBack();
  }
}

async function loadItems() {
  loadingItems.value = true;

  try {
    const response = await ReportVisitItemsService.getAll(reportId.value);
    items.value = response?.data?.data || response?.data || [];
  } catch (error) {
    console.error(error);

    toast(
      "Protege informa:",
      "Não foi possível carregar os itens do relatório.",
      "error"
    );
  } finally {
    loadingItems.value = false;
  }
}

function openCreateItemDrawer() {
  if (!isEditable.value) return;

  selectedItem.value = null;
  isItemDrawerOpen.value = true;
}

function openEditItemDrawer(item) {
  if (!isEditable.value) return;

  selectedItem.value = { ...item };
  isItemDrawerOpen.value = true;
}

function clearSelectedItem() {
  selectedItem.value = null;
}

async function saveItem({ id, payload, isEdit, photoFile, photoPreview }) {
  isSavingItem.value = true;

  try {
    if (!validateItemBeforeSave(payload, photoFile)) {
      isSavingItem.value = false;
      return;
    }

    let savedPhotoPath = payload.photo_path;
    let savedPhotoUrl = payload.photo_url;

    if (photoFile) {
      const photoResponse =
        isEdit && payload.photo_path
          ? await ReportVisitItemsService.replacePhoto(
              photoFile,
              payload.photo_path
            )
          : await ReportVisitItemsService.savePhoto(photoFile);

      const uploadedFile = extractUploadedFile(photoResponse);

      savedPhotoPath = uploadedFile.path || savedPhotoPath;
      savedPhotoUrl = uploadedFile.url || photoPreview || savedPhotoUrl;
    }

    const finalPayload = {
      ...payload,
      photo_path: savedPhotoPath,
      photo_url: savedPhotoUrl,
    };

    if (isEdit) {
      const response = await ReportVisitItemsService.update(
        reportId.value,
        id,
        finalPayload
      );

      const updatedItem = response?.data?.data || response?.data;

      const index = items.value.findIndex((item) => item.id === updatedItem.id);

      if (index !== -1) {
        items.value.splice(index, 1, updatedItem);
      }

      toast("Protege informa:", "Item atualizado com sucesso.", "success");
    } else {
      const response = await ReportVisitItemsService.create(
        reportId.value,
        finalPayload
      );

      const createdItem = response?.data?.data || response?.data;

      items.value.push(createdItem);
      sortItems();

      toast("Protege informa:", "Item criado com sucesso.", "success");
    }

    isItemDrawerOpen.value = false;
    clearSelectedItem();
  } catch (error) {
    console.error(error);

    toast(
      "Protege informa:",
      getFirstValidationError(error) ||
        "Não foi possível salvar o item do relatório.",
      "error"
    );
  } finally {
    isSavingItem.value = false;
  }
}

async function removeItem(item) {
  if (!isEditable.value) return;

  const confirmRemove = window.confirm(
    `Deseja realmente remover o item "${item.title || "sem título"}"?`
  );

  if (!confirmRemove) return;

  try {
    await ReportVisitItemsService.remove(reportId.value, item.id);

    items.value = items.value.filter(
      (currentItem) => currentItem.id !== item.id
    );

    await normalizeOrder();

    toast("Protege informa:", "Item removido com sucesso.", "success");
  } catch (error) {
    console.error(error);

    toast("Protege informa:", "Não foi possível remover o item.", "error");
  }
}

async function moveItem(index, direction) {
  if (!isEditable.value) return;

  const newIndex = index + direction;

  if (newIndex < 0 || newIndex >= items.value.length) return;

  const reorderedItems = [...items.value];
  const [movedItem] = reorderedItems.splice(index, 1);

  reorderedItems.splice(newIndex, 0, movedItem);

  items.value = reorderedItems.map((item, itemIndex) => ({
    ...item,
    order: itemIndex + 1,
  }));

  try {
    const response = await ReportVisitItemsService.reorder(
      reportId.value,
      items.value.map((item) => ({
        id: item.id,
        order: item.order,
      }))
    );

    items.value = response?.data?.data || items.value;
    sortItems();
  } catch (error) {
    console.error(error);

    toast("Protege informa:", "Não foi possível reordenar os itens.", "error");

    await loadItems();
  }
}

async function normalizeOrder() {
  if (!items.value.length) return;

  const payload = items.value.map((item, index) => ({
    id: item.id,
    order: index + 1,
  }));

  try {
    const response = await ReportVisitItemsService.reorder(
      reportId.value,
      payload
    );
    items.value = response?.data?.data || items.value;
    sortItems();
  } catch (error) {
    console.error(error);
  }
}

async function finishReport() {
  if (!validateBeforeFinish()) return;

  const confirmFinish = window.confirm(
    "Deseja realmente finalizar este relatório? Após finalizar, a edição será bloqueada."
  );

  if (!confirmFinish) return;

  try {
    const response = await ReportsService.finish(reportId.value);
    report.value = response?.data?.data || response?.data;

    toast("Protege informa:", "Relatório finalizado com sucesso.", "success");
  } catch (error) {
    console.error(error);

    toast(
      "Protege informa:",
      "Não foi possível finalizar o relatório.",
      "error"
    );
  }
}

async function reopenReport() {
  const confirmReopen = window.confirm(
    "Deseja reabrir este relatório como rascunho para edição?"
  );

  if (!confirmReopen) return;

  try {
    const response = await ReportsService.reopen(reportId.value);
    report.value = response?.data?.data || response?.data;

    toast("Protege informa:", "Relatório reaberto como rascunho.", "success");
  } catch (error) {
    console.error(error);

    toast("Protege informa:", "Não foi possível reabrir o relatório.", "error");
  }
}

function sortItems() {
  items.value = [...items.value].sort((a, b) => {
    return Number(a.order || 0) - Number(b.order || 0);
  });
}

function goBack() {
  router.push({ name: "reports-home" });
}

function formatDate(value) {
  if (!value) return "-";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "-";

  return new Intl.DateTimeFormat("pt-BR").format(date);
}

function getReportTypeLabel(type) {
  const labels = {
    photographic: "Relatório fotográfico",
    descriptive: "Relatório descritivo",
    free_text: "Texto livre",
    photo_text: "Foto + texto",
    checklist: "Checklist",
    action_plan: "Plano de ação",
    audit: "Auditoria",
  };

  return labels[type] || "Relatório técnico";
}

function getFirstValidationError(error) {
  const errors = error?.response?.data?.errors;

  if (!errors) return null;

  const firstKey = Object.keys(errors)[0];

  if (!firstKey) return null;

  return errors[firstKey]?.[0] || null;
}

function extractUploadedFile(response) {
  const data = response?.data || response?.data?.data || {};

  return {
    path:
      data ||
      data.path ||
      data.file_path ||
      data.filePath ||
      data.token ||
      data.name ||
      "",

    url: process.env.VUE_APP_IMAGE_SERVE.concat('storage/').concat(
      data ||
      data.url ||
      data.file_url ||
      data.fileUrl ||
      data.full_url ||
      data.public_url ||
      ""),
  };
}

function isItemWithoutTechnicalContent(item) {
  return ![
    // item.title,
    item.description,
    item.improvement_point,
    item.recommendation,
    // item.technical_observation,
    item.photo_url,
    item.photo_path,
  ]
    .filter(Boolean)
    .some((field) => String(field).trim().length > 0);
}

function validateItemBeforeSave(payload, photoFile) {
  const hasContent = [
    payload.title,
    payload.description,
    payload.improvement_point,
    payload.recommendation,
    payload.technical_observation,
    payload.photo_url,
    payload.photo_path,
    photoFile,
  ]
    .filter(Boolean)
    .some((field) => {
      if (typeof field === "object") return true;

      return String(field).trim().length > 0;
    });

  if (!hasContent) {
    toast(
      "Protege informa:",
      "Informe pelo menos um título, descrição, recomendação ou foto para salvar o item.",
      "info"
    );

    return false;
  }

  return true;
}

function validateBeforeFinish() {
  if (!items.value.length) {
    toast(
      "Protege informa:",
      "Adicione ao menos um item antes de finalizar o relatório.",
      "info"
    );

    return false;
  }

  if (itemStats.value.withoutTechnicalContent > 0) {
    toast(
      "Protege informa:",
      "Existem itens sem conteúdo técnico suficiente. Revise os itens antes de finalizar.",
      "info"
    );

    return false;
  }

  if (hasPhotographicBody.value && itemStats.value.withoutPhoto > 0) {
    return window.confirm(
      `Existem ${itemStats.value.withoutPhoto} item(ns) sem foto. Deseja finalizar mesmo assim?`
    );
  }

  return true;
}

function previewReport() {
  router.push({
    name: "reports-visit-preview",
    params: {
      id: reportId.value,
    },
  });
}
</script>
